/**
 * useActiveCompanyScope — tryb "Wszystkie firmy" (all-companies), client plumbing.
 *
 * Cienka warstwa nad `resolveActiveCompanyScope` (active-company.ts): czyta `me.companies` +
 * `me.activeCompanyId` z `TenantAuthContext`, subskrybuje `subscribeActiveCompany` (pub/sub w
 * active-company.ts) przez `useSyncExternalStore` — re-renderuje sie SYNCHRONICZNIE zaraz po
 * `setStoredActiveCompanyId` (klik w `CompanySwitcher`), bez czekania na kolejny fetch `/me`.
 *
 * `getStoredActiveCompanyId()` sluzy tu jako `getSnapshot` — to prymityw (string | null),
 * wiec React porownuje go referencyjnie/wartosciowo poprawnie miedzy renderami; zmienia sie
 * dokladnie wtedy gdy `setStoredActiveCompanyId` zapisuje nowa wartosc.
 */

import { useSyncExternalStore } from 'react';

import { useTenantAuth } from '@/contexts/TenantAuthContext';

import {
  getStoredActiveCompanyId,
  resolveActiveCompanyScope,
  subscribeActiveCompany,
} from './active-company';

export interface ActiveCompanyScopeResult {
  mode: 'all' | 'company' | 'none';
  /** Konkretne id firmy gdy `mode === 'company'`, inaczej `null`. */
  companyId: string | null;
  /** `true` gdy user jest w trybie "Wszystkie firmy". */
  isAll: boolean;
  /** `true` gdy user ma >= 2 firmy (a wiec opcja "Wszystkie" jest w ogole wyborem). */
  canPickAll: boolean;
}

export function useActiveCompanyScope(): ActiveCompanyScopeResult {
  const { me } = useTenantAuth();
  const companies = me?.companies ?? [];
  const backendActiveId = me?.activeCompanyId ?? null;

  // Subskrypcja zmian localStorage z innego miejsca w drzewie (CompanySwitcher) —
  // getSnapshot zwraca surowa wartosc z localStorage, wiec zmienia sie dokladnie
  // wtedy gdy setStoredActiveCompanyId cos zapisze.
  useSyncExternalStore(subscribeActiveCompany, getStoredActiveCompanyId);

  const scope = resolveActiveCompanyScope(companies, backendActiveId);

  return {
    mode: scope.mode,
    companyId: scope.mode === 'company' ? scope.companyId : null,
    isAll: scope.mode === 'all',
    canPickAll: companies.length >= 2,
  };
}

/**
 * Buduje `?companyId=` dla listy/endpointy ktore juz akceptuja opcjonalny param —
 * `undefined` gdy tryb "Wszystkie" (lub brak firmy w ogole), inaczej konkretne id.
 * Backend interpretuje BRAK parametru jako unie po wszystkich firmach usera — patrz
 * `resolveContractorListVisibility`/`resolveListCompanyScope` po stronie API.
 */
export function companyIdParam(
  scope: Pick<ActiveCompanyScopeResult, 'mode' | 'companyId'>,
): string | undefined {
  return scope.mode === 'company' && scope.companyId ? scope.companyId : undefined;
}
